import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ICreateCustomerRequest,
  ICustomer,
  ICustomerData,
  ICustomerFilter,
  ICustomerPatch,
  IInviteOwnerRequest,
} from 'src/types/customer';

import client from 'src/clients/http';
import { toastMessage } from 'src/utils/toast';
import { IUser } from 'src/types/auth';
import i18n from 'src/i18n';

export const validationCustomerData = createAsyncThunk<ICustomer, ICustomer>(
  'customers/validationCustomerData',
  async (payload, { rejectWithValue }) => {
    try {
      const customer = await client.validationCustomerData(payload);
      return customer;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const createCustomer = createAsyncThunk<ICustomer, ICreateCustomerRequest>(
  'customers/createCustomer',
  async (payload, { rejectWithValue }) => {
    try {
      const customer = await client.createCustomer(payload);
      return customer;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const inviteCustomerOwner = createAsyncThunk<
  { user: IUser; email?: string; userId?: string; customerId: string },
  IInviteOwnerRequest
>('customers/inviteCustomerOwner', async (payload, { rejectWithValue }) => {
  try {
    const user = await client.inviteCustomerOwner(payload);
    toastMessage.success(i18n.t('message.invite.user.success'));
    return { user, ...payload };
  } catch (error: any) {
    toastMessage.error(error?.message);
    return rejectWithValue(error);
  }
});

export const removeCustomerOwner = createAsyncThunk<
  { user?: IUser; customerId: string },
  { user?: IUser; customerId: string }
>('customers/inviteCustomerOwner', async (payload, { rejectWithValue }) => {
  try {
    await client.removeCustomerOwner({
      customerId: payload.customerId,
      alternateOwnerId: payload?.user?.id,
    });

    toastMessage.success(i18n.t('message.deleteUser.success'));
    return payload;
  } catch (error: any) {
    toastMessage.error(error?.message);
    return rejectWithValue(error);
  }
});

export const getCustomers = createAsyncThunk<ICustomer[], { name: '' }>(
  'customers/getAll',
  async (payload, { rejectWithValue }) => {
    try {
      const customers = await client.getCustomers(payload);
      return customers;
    } catch (error: any) {
      toastMessage.error(error?.message);
      return rejectWithValue(error);
    }
  },
);

export const getSingleCustomer = createAsyncThunk<ICustomer, string>(
  `customers/getSingleCustomer`,
  async (id, { rejectWithValue }) => {
    try {
      const customer = await client.getSingleCustomer(id);
      return customer;
    } catch (error: any) {
      toastMessage.error(error?.message);
      return rejectWithValue(error);
    }
  },
);

export const filterCustomer = createAsyncThunk<ICustomerData, ICustomerFilter>(
  'customers/filter',
  async (payload, { rejectWithValue }) => {
    try {
      const customers = await client.filterCustomer(payload);
      return customers;
    } catch (error: any) {
      toastMessage.error(error?.message);
      return rejectWithValue(error);
    }
  },
);

export const updateCustomer = createAsyncThunk<
  { id: string; data: ICustomerPatch },
  { id: string; data: ICustomerPatch }
>('customers/updateCustomer', async (payload, { rejectWithValue }) => {
  try {
    await client.updateCustomer(payload.id, payload.data);
    return payload;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deleteCustomer = createAsyncThunk<string, string>(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await client.deleteCustomer(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
